import { useEffect, useState, useCallback } from "react";
import logger from "../utils/logger";
import axios from "axios";
import { API, getAuthHeaders } from "../context/AuthContext";
import { Trash, Plus, UserCirclePlus, Key } from "@phosphor-icons/react";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import Pagination from "./Pagination";
import ExportButtons from "./ExportButtons";

export default function SlaveAdminManager() {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [creating, setCreating] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize = 8;

  const fetchAdmins = useCallback((p) => {
    setLoading(true);
    axios
      .get(`${API}/admin/slave-admins?page=${p || page}&page_size=${pageSize}`, { headers: getAuthHeaders() })
      .then((res) => {
        setAdmins(res.data.items || []);
        setTotal(res.data.total || 0);
      })
      .catch((err) => logger.error("Slave admins error:", err))
      .finally(() => setLoading(false));
  }, [page]);

  useEffect(() => { fetchAdmins(page); }, [page, fetchAdmins]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!email.trim() || !name.trim()) {
      toast.error("Both name and email are required");
      return;
    }
    if (password && password.length < 8) {
      toast.error("Password must be at least 8 characters (or leave empty for Google-only login)");
      return;
    }
    setCreating(true);
    try {
      const payload = { email: email.trim(), name: name.trim() };
      if (password) payload.password = password;
      await axios.post(`${API}/admin/slave-admins`, payload, { headers: getAuthHeaders() });
      toast.success(password ? "Slave admin created with password" : "Slave admin created (Google sign-in only)");
      setEmail("");
      setName("");
      setPassword("");
      fetchAdmins(page);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create slave admin");
    } finally {
      setCreating(false);
    }
  };

  const handleResetPassword = async (admin) => {
    const newPw = window.prompt(`Set / reset password for ${admin.name}\n(min 8 characters — they'll use this with their Gmail to sign in)`);
    if (!newPw) return;
    if (newPw.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    try {
      await axios.post(
        `${API}/admin/slave-admins/${admin.user_id}/set-password`,
        { new_password: newPw },
        { headers: getAuthHeaders() }
      );
      toast.success(`Password set for ${admin.name}. Share with them securely.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to set password");
    }
  };

  const handleDelete = async (userId) => {
    try {
      await axios.delete(`${API}/admin/slave-admins/${userId}`, { headers: getAuthHeaders() });
      toast.success("Slave admin removed");
      fetchAdmins(page);
    } catch (err) {
      toast.error("Failed to remove slave admin");
    }
  };

  const handleTogglePermission = async (admin, perm) => {
    const current = admin.permissions || ["withdrawals", "kyc", "mt5"];
    const next = current.includes(perm)
      ? current.filter((p) => p !== perm)
      : [...current, perm];
    // Optimistic UI update
    setAdmins((prev) => prev.map((a) => (a.user_id === admin.user_id ? { ...a, permissions: next } : a)));
    try {
      await axios.put(
        `${API}/admin/slave-admins/${admin.user_id}/permissions`,
        { permissions: next },
        { headers: getAuthHeaders() }
      );
      toast.success(`Permissions updated for ${admin.name}`);
    } catch (err) {
      toast.error("Failed to update permissions");
      fetchAdmins(page); // revert
    }
  };

  return (
    <div className="rounded-md border border-white/10 p-5" style={{ background: "#121212" }} data-testid="slave-admin-manager">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-sm font-bold text-white" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Employee (Slave) Management
          </h3>
          <p className="text-[10px] text-[#A1A1AA] mt-0.5">
            Fine-grained permissions — toggle which modules each slave admin can access
          </p>
        </div>
        <ExportButtons exportType="slave-admins" title="Slave Admins" />
      </div>

      <form
        onSubmit={handleCreate}
        className="flex flex-col sm:flex-row gap-3 mb-6 p-4 rounded-md border border-white/10 bg-white/[0.02]"
        data-testid="create-slave-admin-form"
      >
        <div className="flex items-center gap-2 text-[#00E5FF]">
          <UserCirclePlus size={20} weight="duotone" />
        </div>
        <Input
          data-testid="slave-admin-name-input"
          placeholder="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="bg-transparent border-white/10 text-white placeholder:text-[#A1A1AA]/50 text-sm focus-visible:ring-[#00E5FF]"
        />
        <Input
          data-testid="slave-admin-email-input"
          placeholder="Email address"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="bg-transparent border-white/10 text-white placeholder:text-[#A1A1AA]/50 text-sm focus-visible:ring-[#00E5FF]"
        />
        <Input
          data-testid="slave-admin-password-input"
          placeholder="Password (optional, min 8 chars)"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="new-password"
          className="bg-transparent border-white/10 text-white placeholder:text-[#A1A1AA]/50 text-sm focus-visible:ring-[#00E5FF]"
        />
        <button
          type="submit"
          disabled={creating}
          data-testid="create-slave-admin-button"
          className="px-4 py-2 rounded-md text-xs font-bold text-black bg-[#00E5FF] hover:bg-[#00B8CC] transition-colors whitespace-nowrap disabled:opacity-50 flex items-center gap-1.5"
        >
          <Plus size={14} weight="bold" />
          Add Admin
        </button>
      </form>

      {loading ? (
        <div className="h-32 flex items-center justify-center">
          <div className="w-6 h-6 border-2 border-[#00E5FF] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : admins.length === 0 ? (
        <div className="text-center py-10">
          <p className="text-sm text-[#A1A1AA]">No slave admins created yet</p>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-b border-white/10 hover:bg-transparent">
                  <TableHead className="text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA]">Name</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA]">Email</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA]">Role</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA]">Permissions</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA]">Created</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {admins.map((admin) => (
                  <TableRow key={admin.user_id} className="border-b border-white/5 hover:bg-white/[0.03] transition-colors">
                    <TableCell className="text-sm text-white">{admin.name}</TableCell>
                    <TableCell className="text-xs text-[#A1A1AA]">{admin.email}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-[10px] uppercase tracking-wider border bg-[#A78BFA]/10 text-[#A78BFA] border-[#A78BFA]/20">
                        Slave Admin
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5 flex-wrap" data-testid={`perm-cell-${admin.user_id}`}>
                        {[
                          { id: "withdrawals", label: "Withdrawals" },
                          { id: "kyc", label: "KYC" },
                          { id: "mt5", label: "MT5" },
                        ].map((p) => {
                          const on = (admin.permissions || ["withdrawals", "kyc", "mt5"]).includes(p.id);
                          return (
                            <button
                              key={p.id}
                              onClick={() => handleTogglePermission(admin, p.id)}
                              data-testid={`perm-toggle-${admin.user_id}-${p.id}`}
                              className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider border transition-all ${
                                on
                                  ? "bg-[#00E5FF]/10 text-[#00E5FF] border-[#00E5FF]/30 hover:bg-[#00E5FF]/20"
                                  : "bg-white/[0.02] text-[#52525B] border-white/10 hover:border-white/20 hover:text-[#A1A1AA]"
                              }`}
                              title={on ? `Revoke ${p.label}` : `Grant ${p.label}`}
                            >
                              {p.label}
                            </button>
                          );
                        })}
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-[#A1A1AA]">
                      {admin.created_at ? new Date(admin.created_at).toLocaleDateString("en-IN") : "-"}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          data-testid={`reset-password-${admin.user_id}`}
                          onClick={() => handleResetPassword(admin)}
                          title="Set / reset password"
                          className="p-2 rounded-md text-[#F59E0B] hover:bg-[#F59E0B]/10 transition-colors"
                        >
                          <Key size={14} />
                        </button>
                        <button
                          data-testid={`delete-admin-${admin.user_id}`}
                          onClick={() => handleDelete(admin.user_id)}
                          title="Remove admin"
                          className="p-2 rounded-md text-[#EF4444] hover:bg-[#EF4444]/10 transition-colors"
                        >
                          <Trash size={14} />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <Pagination page={page} pageSize={pageSize} total={total} onPageChange={setPage} />
        </>
      )}
    </div>
  );
}
